import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";

// Local Imports for Capability Assessment
import {
  initialFormData,
  CONTROLLED_PAIN_CHOICES,
  MOBILITY_CHOICES,
  SITTING_CHOICES,
  MOVEMENT_IN_BED_CHOICES,
  TOILETING_CHOICES,
  BATHING_CHOICES,
  SKIN_CONDITION_CHOICES,
} from "./capability.config";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import DynamicSelectInput from "../../../../../FormFields/CustomSelect/DynamicSelectInput";
import { QK } from "../../../../../../base/qk";
import { getCarePlans } from "../../../../../../api/services/core";
import { createCapability } from "../../../../../../api/services/assessment";

const CreateCapabilityAssessmentModal = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const { data: carePlans } = useQuery([QK.carePlans], () => getCarePlans());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormData,
  });

  const createAssessmentMutation = useMutation(
    (assessmentData) => createCapability(assessmentData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.capabilityAssessments, clientID]);
        enqueueSnackbar("Capability assessment created successfully", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create assessment",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        client: clientID,
      };

      await createAssessmentMutation.mutateAsync(payload);
      handleCloseModal();
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create Capability Assessment</Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Care Plan Field */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="care_plan"
                  control={control}
                  rules={{ required: "Care plan is required" }}
                  render={({ field }) => (
                    <DynamicSelectInput
                      options={carePlans}
                      label="Which care plan is this linked to? *"
                      name="care_plan"
                      onChange={field.onChange}
                      value={field.value}
                      error={!!errors.care_plan}
                      helperText={errors.care_plan?.message}
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Assessment Section */}
            <Typography variant="h6" gutterBottom>
              Capability Assessment
            </Typography>

            <Grid container spacing={2}>
              {/* controlled_pain */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="controlled_pain"
                  control={control}
                  rules={{ required: "Controlled pain assessment is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      name="controlled_pain"
                      label="Controlled Pain *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={CONTROLLED_PAIN_CHOICES}
                      error={!!errors.controlled_pain}
                      helperText={errors.controlled_pain?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {/* skin_condition */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="skin_condition"
                  control={control}
                  rules={{ required: "Skin condition assessment is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      name="skin_condition"
                      label="Skin Condition *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={SKIN_CONDITION_CHOICES}
                      error={!!errors.skin_condition}
                      helperText={errors.skin_condition?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {/* mobility_standing */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="mobility_standing"
                  control={control}
                  rules={{
                    required: "Mobility/Standing assessment is required",
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      name="mobility_standing"
                      label="Mobility/Standing *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={MOBILITY_CHOICES}
                      error={!!errors.mobility_standing}
                      helperText={errors.mobility_standing?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {/* walking */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="walking"
                  control={control}
                  rules={{ required: "Walking assessment is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      name="walking"
                      label="Walking *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={MOBILITY_CHOICES} // Reusing mobility choices
                      error={!!errors.walking}
                      helperText={errors.walking?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {/* sitting */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="sitting"
                  control={control}
                  rules={{ required: "Sitting assessment is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      name="sitting"
                      label="Sitting *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={SITTING_CHOICES}
                      error={!!errors.sitting}
                      helperText={errors.sitting?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {/* movement_in_bed */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="movement_in_bed"
                  control={control}
                  rules={{ required: "Movement in bed assessment is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      name="movement_in_bed"
                      label="Movement in Bed *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={MOVEMENT_IN_BED_CHOICES}
                      error={!!errors.movement_in_bed}
                      helperText={errors.movement_in_bed?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {/* toileting_continence */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="toileting_continence"
                  control={control}
                  rules={{
                    required: "Toileting/Continence assessment is required",
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      name="toileting_continence"
                      label="Toileting/Continence *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={TOILETING_CHOICES}
                      error={!!errors.toileting_continence}
                      helperText={errors.toileting_continence?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {/* bathing */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="bathing"
                  control={control}
                  rules={{ required: "Bathing assessment is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      name="bathing"
                      label="Bathing *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={BATHING_CHOICES}
                      error={!!errors.bathing}
                      helperText={errors.bathing?.message}
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Comments */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="comments"
                      label="Comments"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={4}
                      placeholder="Add any relevant comments or notes here."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={createAssessmentMutation.isLoading}
        >
          {createAssessmentMutation.isLoading
            ? "Creating..."
            : "Create Assessment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCapabilityAssessmentModal;
